import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Zoom from "@mui/material/Zoom";
import IconButton from "@mui/material/IconButton";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import {
  RoutesEnum,
  RoutingIDEnum,
  RoutingAnchorEnum,
  NavMenuEnum,
} from "../../types/types";
import { EntainLogoButton } from "src/library/components/exports";
import DesktopHeaderLinks from "./DesktopHeaderLinks";
import { sectionBackgroundColors, zIndexes } from "src/library/styles/theme";

import { useLocation, useNavigate } from "react-router-dom";

interface HeaderProps {
  children: React.ReactElement;
}

function ElevationScroll(props: HeaderProps) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
}

function ScrollTop(props: HeaderProps) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (
      (event.target as HTMLDivElement).ownerDocument || document
    ).querySelector(RoutingAnchorEnum.TopAnchor);

    if (anchor) {
      anchor.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };
  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: "fixed", bottom: 20, right: 20, zIndex: zIndexes.Header }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

export default function Header(props: HeaderProps) {
  const { children } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const showMobileMenu = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <>
      <ElevationScroll {...props}>
        <AppBar sx={{ backgroundColor: sectionBackgroundColors.header }}>
          <Container maxWidth="xl" disableGutters={showMobileMenu}>
            <Toolbar>
              {showMobileMenu ? (
                <>
                  <IconButton
                    size="large"
                    edge="start"
                    aria-label="menu"
                    onClick={() =>
                      pathname === RoutesEnum.Nav
                        ? navigate(-1)
                        : navigate(RoutesEnum.Nav)
                    }
                    sx={{ zIndex: zIndexes.Header, color: "common.white" }}
                  >
                    {pathname === RoutesEnum.Nav ? (
                      <CloseIcon fontSize="large" />
                    ) : (
                      <MenuIcon fontSize="large" />
                    )}
                  </IconButton>
                  <EntainLogoButton section={NavMenuEnum.Home} />
                </>
              ) : (
                <DesktopHeaderLinks />
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </ElevationScroll>
      <Toolbar id={RoutingIDEnum.TopAnchor} sx={{ height: 0, minHeight: 0 }} />
      {/* SHOW CHILDREN OF HEADER */}
      {children}
      <ScrollTop {...props}>
        <Fab
          color="secondary"
          size="small"
          aria-label="scroll back to top"
          sx={{
            opacity: 0.8,
            "&:hover": {
              backgroundColor: "#0CD6FF",
              opacity: 1,
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </>
  );
}
